import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "overlay", "calendar", "monthLabel", "hiddenInput", "selectedDisplay"]
  static values = {
    maxDate: { type: String, default: "" },
    selectedDate: { type: String, default: "" },
    readonly: { type: Boolean, default: false }
  }

  connect() {
    this.currentYear = new Date().getFullYear()
    this.currentMonth = new Date().getMonth()
    this.today = this.formatDate(new Date())

    if (!this.maxDateValue) {
      this.maxDateValue = this.today
    }

    // 기존 값이 있으면 반영
    if (this.hasHiddenInputTarget && this.hiddenInputTarget.value) {
      this.selectedDateValue = this.hiddenInputTarget.value
      const [y, m] = this.selectedDateValue.split("-")
      this.currentYear = parseInt(y)
      this.currentMonth = parseInt(m) - 1
      this.updateDisplay()
    }
  }

  open() {
    if (this.readonlyValue) return
    if (this.hasOverlayTarget) {
      this.overlayTarget.style.display = "flex"
      // 애니메이션을 위해 requestAnimationFrame 사용
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.overlayTarget.classList.add("custom-date-picker__overlay--active")
        })
      })
      document.body.style.overflow = "hidden"
      this.renderCalendar()
    }
  }

  close() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("custom-date-picker__overlay--active")
      setTimeout(() => {
        this.overlayTarget.style.display = "none"
      }, 300)
      document.body.style.overflow = ""
    }
  }

  closeOnOverlay(event) {
    if (event.target === this.overlayTarget) {
      this.close()
    }
  }

  prevMonth() {
    this.currentMonth--
    if (this.currentMonth < 0) {
      this.currentMonth = 11
      this.currentYear--
    }
    this.renderCalendar()
  }

  nextMonth() {
    const now = new Date()
    const nextYear = this.currentMonth === 11 ? this.currentYear + 1 : this.currentYear
    const nextMon = this.currentMonth === 11 ? 0 : this.currentMonth + 1
    // maxDate 월까지만 이동 가능
    if (this.maxDateValue) {
      const [maxY, maxM] = this.maxDateValue.split("-").map(Number)
      if (nextYear > maxY || (nextYear === maxY && nextMon + 1 > maxM)) {
        return
      }
    }
    this.currentMonth++
    if (this.currentMonth > 11) {
      this.currentMonth = 0
      this.currentYear++
    }
    this.renderCalendar()
  }

  selectDate(event) {
    const dateStr = event.currentTarget.dataset.date
    if (!dateStr) return
    this.selectedDateValue = dateStr
    this.renderCalendar()
  }

  confirm() {
    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = this.selectedDateValue
      this.hiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
    this.updateDisplay()
    this.close()
  }

  reset() {
    this.selectedDateValue = ""
    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = ""
      this.hiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
    this.updateDisplay()
    this.renderCalendar()
  }

  renderCalendar() {
    if (!this.hasCalendarTarget) return

    this.updateMonthLabel()
    this.updateNavButtons()

    const firstDay = new Date(this.currentYear, this.currentMonth, 1).getDay()
    const daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate()

    let html = ""

    // 빈 셀 (1일 전)
    for (let i = 0; i < firstDay; i++) {
      html += '<div class="custom-date-picker__day custom-date-picker__day--empty"></div>'
    }

    // 날짜 셀
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = this.formatDate(new Date(this.currentYear, this.currentMonth, day))
      const classes = ["custom-date-picker__day"]

      if (dateStr === this.today) {
        classes.push("custom-date-picker__day--today")
      }
      if (dateStr === this.selectedDateValue) {
        classes.push("custom-date-picker__day--selected")
      }
      if (this.maxDateValue && dateStr > this.maxDateValue) {
        classes.push("custom-date-picker__day--disabled")
      }

      html += `<button type="button" class="${classes.join(" ")}" data-date="${dateStr}" data-action="click->custom-date-picker#selectDate">${day}</button>`
    }

    this.calendarTarget.innerHTML = html
  }

  updateMonthLabel() {
    if (this.hasMonthLabelTarget) {
      this.monthLabelTarget.textContent = `${this.currentYear}년 ${this.currentMonth + 1}월`
    }
  }

  updateNavButtons() {
    // nextMonth 버튼 비활성화 체크
    const nextBtn = this.element.querySelector("[data-action*='nextMonth']")
    if (nextBtn && this.maxDateValue) {
      const [maxY, maxM] = this.maxDateValue.split("-").map(Number)
      const nextYear = this.currentMonth === 11 ? this.currentYear + 1 : this.currentYear
      const nextMon = this.currentMonth === 11 ? 1 : this.currentMonth + 2
      if (nextYear > maxY || (nextYear === maxY && nextMon > maxM)) {
        nextBtn.classList.add("custom-date-picker__nav-btn--disabled")
      } else {
        nextBtn.classList.remove("custom-date-picker__nav-btn--disabled")
      }
    }
  }

  updateDisplay() {
    if (!this.hasSelectedDisplayTarget) return

    if (this.selectedDateValue) {
      const [y, m, d] = this.selectedDateValue.split("-")
      this.selectedDisplayTarget.textContent = `${y}년 ${parseInt(m)}월 ${parseInt(d)}일`
      this.selectedDisplayTarget.classList.add("custom-date-picker__display--has-value")
    } else {
      this.selectedDisplayTarget.textContent = "날짜를 선택해주세요"
      this.selectedDisplayTarget.classList.remove("custom-date-picker__display--has-value")
    }

    // has-value 클래스 토글 (기존 validation 호환)
    const wrapper = this.element.closest(".conflict-resolution-form__date-placeholder")
    if (wrapper) {
      wrapper.classList.toggle("has-value", !!this.selectedDateValue)
    }
  }

  formatDate(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }
}
